import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container } from 'styled-bootstrap-grid';
import { SectionWrapper, Heading, GuestField } from '@common/components/BookingForms';

import { FieldArray, reduxForm, formValueSelector } from 'redux-form';

import { useTranslations } from '@lang';
import { Button, StickyBarContent, StickyBarWrapper } from '@common/components';

import { AddExistingGuests } from '@common/components/AddExistingGuests';

import { guestSelectors } from '@store/guest/selectors';

import { required } from '@utils/validators';
import { useLabels } from '@features/Menu/useLabels';

const validate = (values) => {
  const errors = {};

  if (!values.guests || !values.guests.length) {
    errors.guests = {
      _error: required(),
    };
  } else {
    errors.guests = values.guests.map((guest) => ({
      title: required(guest.title),
      firstName: required(guest.firstName),
      lastName: required(guest.lastName),
    }));
  }

  return errors;
};

const GuestDetailsStep = ({
  basketGuests,
  loggedGuest,
  handleSubmit,
  change,
  selectedGuests,
  valid,
  submitting,
  submit,
  totalGuestAmount,
  isGenderSpecific,
  spaClass,
  timeSlotPrice,
  handleSignInModalOpen,
  isSignInModalOpen = false,
  showGenderNeutral,
  discount,
}) => {
  const { t } = useTranslations();
  const labels = useLabels();

  const displayedPrice =
    selectedGuests && selectedGuests.length > 1
      ? {
          ...timeSlotPrice,
          amount: selectedGuests.length * timeSlotPrice.amount,
        }
      : timeSlotPrice;

  const discountedPrice =
    selectedGuests && selectedGuests.length > 1
      ? {
          discountedPrice: {
            ...discount?.discountedPrice,
            amount: selectedGuests.length * discount?.discountedPrice.amount,
          },
        }
      : discount;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Container fluid>
        <Heading>{t('common.guestDetails')}</Heading>
        <SectionWrapper>
          {t('common.classGuestAmountDescription', {
            guestAmount: totalGuestAmount,
          })}
          <FieldArray
            name="guests"
            component={AddExistingGuests}
            selectedGuests={selectedGuests}
            existingGuests={basketGuests}
            isGenderSpecific={isGenderSpecific}
            minimumGuests={1}
            maximumGuests={totalGuestAmount}
            guest={loggedGuest}
            withTopMargin
          />
          <FieldArray
            component={GuestField}
            existingGuests={basketGuests}
            fieldChange={change}
            maximumGuests={totalGuestAmount}
            minimumGuests={1}
            name="guests"
            selectedGuests={selectedGuests}
            isGenderSpecific={isGenderSpecific}
            guest={loggedGuest}
            handleSignInModalOpen={handleSignInModalOpen}
            isSignInModalOpen={isSignInModalOpen}
            showGenderNeutral={showGenderNeutral}
          />
        </SectionWrapper>
      </Container>
      <StickyBarWrapper>
        <StickyBarContent
          name={spaClass.name}
          subtitle={labels?.classes || t('common.classes')}
          displayedPrice={displayedPrice}
          discountedPrice={discountedPrice}
          button={
            <Button
              isPrimary
              type="button"
              onClick={() => submit('classConfigurationForm')}
              disabled={!valid || submitting}
            >
              {t('roomPage.bookRoomCTA')}
            </Button>
          }
        />
      </StickyBarWrapper>
    </form>
  );
};

GuestDetailsStep.propTypes = {
  loggedGuest: PropTypes.shape({
    title: PropTypes.string,
  }).isRequired,
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  basketGuests: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalGuestAmount: PropTypes.number.isRequired,
  spaClass: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  timeSlotPrice: PropTypes.object.isRequired,
  valid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
  selectedGuests: PropTypes.array.isRequired,
  isGenderSpecific: PropTypes.string.isRequired,
  isSignInModalOpen: PropTypes.bool,
  handleSignInModalOpen: PropTypes.func.isRequired,
};

const selector = formValueSelector('classConfigurationForm');

const mapStateToProps = (state) => ({
  selectedGuests: selector(state, 'guests'),
  loggedGuest: guestSelectors.getGuest(state),
});

const GuestDetailsStepForm = reduxForm({
  form: 'classConfigurationForm',
  destroyOnUnmount: true,
  forceUnregisterOnUnmount: true,
  validate,
})(GuestDetailsStep);

export default connect(mapStateToProps)(GuestDetailsStepForm);
